import type { StudioSystemType } from 'types/global'
import type { HardwareDetailType } from '../types'

// Abstract protocol for component detail snapshots
export abstract class ISystemHardwareDetail {
  abstract get modules(): HardwareDetailType[]
  abstract get inverters(): HardwareDetailType[]
  abstract get batteries(): HardwareDetailType[]
  abstract get others(): HardwareDetailType[]
}

// System design component detail snapshot
export class SystemDesignComponentDetailSnapshot implements ISystemHardwareDetail {
  private _modules?: HardwareDetailType[]
  private _inverters?: HardwareDetailType[]
  private _batteries?: HardwareDetailType[]
  private _others?: HardwareDetailType[]
  private _system: StudioSystemType

  constructor(system: StudioSystemType) {
    this._system = system
  }

  static injectPriceOverride = ({
    system,
    hardwares,
  }: {
    system: StudioSystemType
    hardwares: HardwareDetailType[]
  }): HardwareDetailType[] => {
    const cogsOverrides = system.getCogsOverride() || {}
    hardwares.forEach((item, index) => {
      const override = item?.activated_id ? cogsOverrides[item?.activated_id] : null
      const priceSource = override?.source || 'unknown'
      item['priceSource'] = priceSource
    })
    return hardwares
  }

  static getModulesDetailFromSystem = (system: StudioSystemType): HardwareDetailType[] => {
    const hardwares: HardwareDetailType[] = []
    system.getModuleTypes().forEach((mt) => {
      hardwares.push({
        activated_id: mt.componentId,
        code: mt.code,
        quantity: mt.getModuleQuantity(),
        type: 'module',
      })
    })

    return SystemDesignComponentDetailSnapshot.injectPriceOverride({ system, hardwares })
  }

  static getInverterDetailFromSystem = (system: StudioSystemType): HardwareDetailType[] => {
    const hardwares: HardwareDetailType[] = []

    const inverters = system.inverters()
    inverters.forEach((inverter) => {
      const quantity = inverter.getQuantityFromSystemLayout()
      hardwares.push({
        activated_id: inverter.inverter_id,
        code: inverter.code,
        quantity: quantity,
        type: 'inverter',
      })
    })
    return SystemDesignComponentDetailSnapshot.injectPriceOverride({ system, hardwares })
  }

  static getBatteriesDetailFromSystem = (system: StudioSystemType): HardwareDetailType[] => {
    const hardwares: HardwareDetailType[] = []
    const batteries = system.batteries()
    batteries.forEach((battery) => {
      const existingComponent = hardwares.find((component) => component.code === battery.code)
      if (existingComponent) {
        existingComponent.quantity += battery.quantity
      } else {
        hardwares.push({
          activated_id: battery.battery_id,
          code: battery.code,
          quantity: battery.quantity,
          type: 'battery',
        })
      }
    })

    return SystemDesignComponentDetailSnapshot.injectPriceOverride({ system, hardwares })
  }

  static getOthersDetailFromSystem = (system: StudioSystemType): HardwareDetailType[] => {
    const hardwares: HardwareDetailType[] = []
    const others = system.others()
    others.forEach((other) => {
      hardwares.push({
        activated_id: other.other_id ?? undefined,
        code: other.code,
        quantity: other.quantity,
        type: 'other',
      })
    })

    return SystemDesignComponentDetailSnapshot.injectPriceOverride({ system, hardwares })
  }

  get modules(): HardwareDetailType[] {
    if (this._modules) return this._modules
    this._modules = SystemDesignComponentDetailSnapshot.getModulesDetailFromSystem(this._system)
    return this._modules
  }

  get inverters(): HardwareDetailType[] {
    if (this._inverters) return this._inverters

    this._inverters = SystemDesignComponentDetailSnapshot.getInverterDetailFromSystem(this._system)
    return this._inverters
  }

  get batteries(): HardwareDetailType[] {
    if (this._batteries) return this._batteries

    this._batteries = SystemDesignComponentDetailSnapshot.getBatteriesDetailFromSystem(this._system)
    return this._batteries
  }

  get others(): HardwareDetailType[] {
    if (this._others) return this._others

    this._others = SystemDesignComponentDetailSnapshot.getOthersDetailFromSystem(this._system)
    return this._others
  }

  get allSystemHardware(): HardwareDetailType[] {
    return [...this.modules, ...this.inverters, ...this.batteries, ...this.others]
  }
}
